import { BigTitleNavigation } from '@dreamstack/accentro-contentful/src/blocks/BigTitleNavigation'
import { ChildrenBlockSpacingContainer } from '@dreamstack/accentro-contentful/src/contentful/ChildrenBlockSpacingContainer'
import { ContentfulBlocks } from '@dreamstack/accentro-contentful/src/contentful/ContentfulBlocks'
import {
  OtherLocaleUrlsContextProvider,
  useFormat,
  useLocale,
  useTranslation,
} from '@dreamstack/i18n'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import { ArticleRichMarkup, DreamstackSeo } from '@dreamstack/seo'
import { AdditionalTrackingInfoContextProvider } from '@dreamstack/tracking'
import { RouterOutputs } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import map from 'lodash/map'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'
import { FeaturedArticleTemplate } from './FeaturedArticleTemplate'
import { MainLayout } from '../layout/MainLayout'
import { NewsArticleContact } from '../layout/NewsArticleContact'
import { GridWithGaps, GridWithGapsNoCols } from '../layout/MainGrid'
import {
  Css_HeadingH4_tw,
  HeadingH1,
  HeadingH2,
} from '../layout/Texts'
import { PageTag } from '../layout/PageTag'
import { NewsArticleFile } from './NewsArticleFile'
import { getNewsPathOtherLocales, newsBaseUrls } from './getNewsPath'

const OtherArticlesLayout = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn('grid col-span-full grid-cols-1 md:grid-cols-2', className)}
    {...rest}
  >
    {children}
  </div>
)

const DateWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'border-t-2 border-accentroGray-full mt-px-48 ml:mt-px-64 lg:mt-px-80',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

// Responsive grid template recreated via a data attribute + inline <style> for precise area control
const newsArticleGridCss = `
[data-news-article-grid] {display:grid !important;grid-template:
  'type type'
  'title title'
  'image image'
  'date .'
  'file file'
  'article article'
  'more more' / 1fr 1fr !important;}
/* md breakpoint ~768px */
@media (min-width: 768px){
  [data-news-article-grid]{grid-template:
    'type type type type'
    'title title title title'
    'date image image image'
    'file file file file'
    'article article article article'
    'more more more more' / 1fr 1fr 1fr 1fr !important;}
}
/* ml breakpoint ~1366px (custom) */
@media (min-width: 1366px){
  [data-news-article-grid]{grid-template:
    'type type type type'
    'title title title title'
    'date image image image'
    '. file file file'
    '. article article article'
    'more more more more' / 1fr 1fr 1fr 1fr !important;}
}
`

const NewsArticleGrid = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <>
    <style dangerouslySetInnerHTML={{ __html: newsArticleGridCss }} />
    <GridWithGapsNoCols
      data-news-article-grid
      className={cn(className)}
      {...rest}
    >
      {children}
    </GridWithGapsNoCols>
  </>
)
const StyledDate = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn('block md:block md:col-start-1 md:col-span-2', className)}
    {...rest}
  >
    {children}
  </div>
)

export type NewsArticleProps = {
  article: RouterOutputs['cms']['getNewsArticle']['article']
  otherArticles: RouterOutputs['cms']['getNewsArticle']['otherArticles']
  blocks: RouterOutputs['cms']['getNewsArticle']['blocks']
  otherLocaleArticles: RouterOutputs['cms']['getNewsArticle']['otherLocaleArticles']
}

const MonthAndYear = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <h4
    className={cn(
      Css_HeadingH4_tw,
      'mt-px-8 break-normal w-px-64 font-semibold',
      className
    )}
    {...rest}
  >
    {children}
  </h4>
)

export const NewsArticle: FunctionComponent<
  React.PropsWithChildren<NewsArticleProps>
> = ({ article, otherArticles, otherLocaleArticles, blocks }) => {
  assertPopulatedDeep(article)
  assertPopulatedDeep(otherLocaleArticles)

  const t = useTranslation()
  const formatDate = useFormat()
  const formattedDays = article.date ? formatDate(article.date, 'dd.') : null
  const formattedMonthAndYear = article.date
    ? formatDate(article.date, 'MMMM yyyy')
    : null

  // const translatedNewsTypePlural = t(
  //   `accentro:newsTypes.${article?.newsType}`,
  //   {
  //     count: 2,
  //   }
  // )
  const translatedNewsTypeSingular = t(
    `accentro:newsTypes.${article?.newsType}`
  )

  const locale = useLocale()
  const newsBaseUrl = newsBaseUrls[locale]

  const additionalInfoMemoized = useMemo(
    () => ({
      newsType: article.newsType,
      slug: article.slug,
      articleId: article.id,
      title: article.title,
    }),
    [article.newsType, article.slug, article.id, article.title]
  )

  // const { onHoverStart, controls } = useAnimateIconTranslationRight()
  return (
    <OtherLocaleUrlsContextProvider
      context={getNewsPathOtherLocales(
        otherLocaleArticles?.de,
        otherLocaleArticles?.en
      )}
    >
      <AdditionalTrackingInfoContextProvider context={additionalInfoMemoized}>
        <DreamstackSeo
          title={article.title ?? ''}
          image={
            !!article.image?.url && !!article.image?.title
              ? { url: article.image?.url, alt: article.image?.title }
              : undefined
          }
          description={article.previewText ?? ''}
        />
        <ArticleRichMarkup article={article} />
        <MainLayout navbarProps={{ forceDarkTextColor: true }}>
          <NewsArticleGrid>
            <div className="mt-px-64 [grid-area:type]">
              <PageTag text={translatedNewsTypeSingular}></PageTag>
            </div>
            <div className="mt-px-32 [grid-area:title]">
              <HeadingH1
                className="[hyphens:auto]"
                dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
              />
            </div>
            <div className="mt-px-32 [grid-area:file]">
              <NewsArticleFile file={article.file} />
            </div>
            {!!article.image && !!article.image.url && (
              <div
                className={cn(
                  'aspect-[3/2] relative mt-px-48 ml:mt-px-64 lg:mt-px-80 [grid-area:image]'
                )}
              >
                <Image
                  src={article.image.url}
                  fill
                  style={{ objectFit: 'cover' }}
                  alt={article.image.alt || 'Newsarticle image'}
                />
              </div>
            )}
            {article.date && !article.hideDate && (
              <DateWrapper className="[grid-area:date]">
                <StyledDate>
                  <HeadingH2 className="mt-px-12">{formattedDays}</HeadingH2>
                  <MonthAndYear>{formattedMonthAndYear}</MonthAndYear>
                </StyledDate>
              </DateWrapper>
            )}
            <div className="[grid-area:article] mt-px-48 ml:mt-px-64 lg:mt-px-80">
              {!!blocks && (
                <ChildrenBlockSpacingContainer>
                  <ContentfulBlocks blocks={blocks} />
                </ChildrenBlockSpacingContainer>
              )}
              <div className="grid grid-cols-1 mt-px-48 lg:grid-cols-2 lg:gap-px-64">
                {map(article.contacts, (contact, i) => {
                  // Skip if contact is not populated (is just an ID number)
                  if (!contact || typeof contact === 'number') return null
                  return (
                    <NewsArticleContact
                      contact={contact}
                      key={contact.id ?? i}
                    />
                  )
                })}
              </div>
            </div>
            <div className="[grid-area:more]">
              <GridWithGaps>
                <OtherArticlesLayout className="border-t-2" />
              </GridWithGaps>
              {!!otherArticles?.length && (
                <div className="mt-px-48">
                  <BigTitleNavigation
                    block={{
                      title: t('accentro:newsHub.readMoreTitle'),
                      buttonText: t('accentro:newsHub.allTopics'),
                      staticPageReference: { slug: newsBaseUrl },
                    }}
                  />
                  <GridWithGaps>
                    <OtherArticlesLayout>
                      {map(otherArticles, (article) => {
                        const slug = article.slug
                        return (
                          <FeaturedArticleTemplate
                            key={slug}
                            newsArticle={article}
                          />
                        )
                      })}
                    </OtherArticlesLayout>
                  </GridWithGaps>
                </div>
              )}
            </div>
          </NewsArticleGrid>
        </MainLayout>
      </AdditionalTrackingInfoContextProvider>
    </OtherLocaleUrlsContextProvider>
  )
}
